import type { UsuarioSesion } from '@kodica/shared';

/**
 * A dónde hay que mandar a alguien antes de dejarle usar la app, o `null` si ya
 * está donde puede estar.
 *
 * ## Por qué esto es una función y no tres `if` dentro del componente
 *
 * Porque eran tres `if` dentro del componente y se mandaban el uno al otro.
 *
 * El SUPER_ADMIN recién creado tiene las dos condiciones a la vez: contraseña
 * provisional **y** ningún condominio. Con las reglas escritas como guardias
 * independientes, cada una desviaba a la pantalla que la otra rechazaba:
 *
 *     /                → /contrasena       (falta cambiar la contraseña)
 *     /contrasena      → /fraccionamiento  (no hay condominio activo)
 *     /fraccionamiento → /contrasena       (falta cambiar la contraseña)
 *
 * React Router corta el ciclo dejando la pantalla **en blanco**, sin error en
 * la consola y sin una petición fallida que mirar. Desde fuera es
 * indistinguible de una app rota.
 *
 * Sacarlo aquí no es limpieza: es lo que permite comprobar que las reglas
 * **convergen**, que es la propiedad que se rompió. Un `if` dentro de un
 * componente no se puede recorrer; una función sí.
 */
export function aDondeMandar(usuario: UsuarioSesion, ruta: string): string | null {
  // **Con una contraseña que eligió otro, no se hace nada más** — y «nada más»
  // incluye elegir fraccionamiento. El padrón la reparte provisional y la marca
  // así; dejar entrar sin cambiarla convertiría esa provisional en la
  // definitiva de esa persona, dictada por teléfono y conocida por quien la
  // generó.
  //
  // El `return` temprano es lo que rompe el ciclo: mientras esto esté
  // pendiente, ninguna otra regla opina.
  if (usuario.debeCambiarPassword) {
    return ruta === '/contrasena' ? null : '/contrasena';
  }

  // **Sin fraccionamiento activo no hay nada que enseñar.** Le pasa al
  // SUPER_ADMIN de Kodica, que no pertenece a ninguno: el API le niega el
  // padrón, la topología y todo lo demás —con razón, porque elegirle uno por él
  // sería peor—. Sin este desvío vería un menú completo donde cada pantalla
  // responde 403, que se lee como un sistema roto y no como una elección
  // pendiente.
  if (usuario.condominios.length === 0 && ruta !== '/fraccionamiento') {
    return '/fraccionamiento';
  }

  return null;
}
